import type { BcStoreUserRecord } from '../services/api';

export interface PermissionMatrixColumnMeta {
  accessType: 'read' | 'write' | 'unknown';
  isWrite: boolean;
}

export interface PermissionMatrixRow {
  storeHash: string;
  storeName: string;
  email: string;
  status: string;
  cells: Record<string, boolean>;
}

export interface PermissionMatrix {
  permissionColumns: string[];
  columnMeta: Record<string, PermissionMatrixColumnMeta>;
  rows: PermissionMatrixRow[];
}

export function buildPermissionMatrix(
  users: BcStoreUserRecord[],
  options: { writeOnly?: boolean } = {},
): PermissionMatrix {
  const columnMeta = new Map<string, PermissionMatrixColumnMeta>();

  for (const user of users) {
    for (const permission of user.permissions) {
      if (!columnMeta.has(permission.code)) {
        columnMeta.set(permission.code, {
          accessType: permission.accessType,
          isWrite: permission.isWrite,
        });
      } else if (permission.isWrite) {
        columnMeta.set(permission.code, {
          accessType: permission.accessType,
          isWrite: true,
        });
      }
    }
  }

  let permissionColumns = Array.from(columnMeta.keys()).sort((a, b) => a.localeCompare(b));
  if (options.writeOnly) {
    permissionColumns = permissionColumns.filter((code) => columnMeta.get(code)?.isWrite);
  }

  const rows = users
    .slice()
    .sort((a, b) => a.email.localeCompare(b.email))
    .map((user) => {
      const granted = new Set(user.permissions.map((permission) => permission.code));
      const cells: Record<string, boolean> = {};
      for (const code of permissionColumns) {
        cells[code] = granted.has(code);
      }
      return {
        storeHash: user.storeHash,
        storeName: user.storeName,
        email: user.email,
        status: user.status,
        cells,
      };
    });

  const metaRecord: Record<string, PermissionMatrixColumnMeta> = {};
  for (const code of permissionColumns) {
    const meta = columnMeta.get(code);
    if (meta) {
      metaRecord[code] = meta;
    }
  }

  return {
    permissionColumns,
    columnMeta: metaRecord,
    rows,
  };
}

export function matrixCellValue(granted: boolean): string {
  return granted ? 'X' : '';
}
